import { useFeatureUsage } from '@/hooks/useUsageLimits';
import { FeatureKey } from '@/types/subscription';
import { useLanguage } from '@/contexts';

interface UsageIconProps {
  featureKey: FeatureKey;
  mockData?: {
    current_usage: number;
    limit_value: number | null;
    percentage_used: number | null;
    isUnlimited: boolean;
  };
}

/**
 * UsageIcon
 * Compact icon indicator for header - shows colored circle with percentage and feature name
 */
export function UsageIcon({ featureKey, mockData }: UsageIconProps) {
  const fetchedUsage = useFeatureUsage(featureKey);
  const usage = mockData || fetchedUsage;
  const { t } = useLanguage();

  if (!usage) {
    return null;
  }

  const isUnlimited = usage.limit_value === null;
  const percentage = usage.percentage_used || 0;

  // Determine color based on percentage
  const getStatusColor = () => {
    if (percentage >= 90) return 'bg-red-500 dark:bg-red-400';
    if (percentage >= 75) return 'bg-yellow-500 dark:bg-yellow-400';
    return 'bg-green-500 dark:bg-green-400';
  };

  const getStatusLabel = () => {
    if (percentage >= 90) return t('usageWidget.status.critical');
    if (percentage >= 75) return t('usageWidget.status.warning');
    return t('usageWidget.status.healthy');
  };

  // Format feature key for display using i18n
  const formatFeatureKey = (key: FeatureKey): string => {
    return t(`usageWidget.features.${key}`);
  };

  const featureName = formatFeatureKey(featureKey);

  return (
    <div className="relative group">
      {/* Icon button */}
      <button 
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 transition-all duration-200"
        aria-label={t('usageWidget.aria.usageIndicator').replace('{{feature}}', featureName)}
      >
        <div className={`w-2.5 h-2.5 rounded-full ${getStatusColor()} animate-pulse`}></div>
        <span className="text-xs font-semibold text-gray-700 dark:text-slate-200 transition-colors duration-300">
          {isUnlimited ? '∞' : t('usageWidget.status.percentage').replace('{{percentage}}', String(percentage))}
        </span>
      </button>

      {/* Tooltip on hover - shows feature name and usage */}
      <div className="absolute top-full right-0 mt-2 hidden group-hover:block z-50">
        <div className="bg-gray-900 dark:bg-slate-900 text-white dark:text-slate-100 text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap transition-colors duration-300">
          <div className="font-semibold mb-1">{featureName}</div>
          {isUnlimited ? (
            <span className="text-gray-300 dark:text-slate-300 transition-colors duration-300">
              {t('usageWidget.title')}: {t('usageWidget.status.unlimited')}
            </span>
          ) : (
            <span className="text-gray-300 dark:text-slate-300 transition-colors duration-300">
              {getStatusLabel()} - {t('usageWidget.status.limited')
                .replace('{{current}}', String(usage.current_usage))
                .replace('{{limit}}', String(usage.limit_value))}
            </span>
          )}
          <div className="absolute -top-1 right-4 w-2 h-2 bg-gray-900 dark:bg-slate-900 transform rotate-45 transition-colors duration-300"></div>
        </div>
      </div>
    </div>
  );
}

